import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  CARD_ROLE_ARTICLE,
  CARD_ROLE_LABEL,
  NOYAU_MODE_LABEL,
  type WalkNoyauSite,
  noyauLabel,
  takenCard,
} from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import {
  type NoyauCode,
  type NoyauGapLineState,
  type RichText,
  type SessionNoyauReading,
  eliminatedNoyaux,
  noyauGapLineOf,
  noyauOf,
  noyauPoints,
  oppositeNoyau,
  renderRichText,
} from '@matter_extractor/shared';
import { Check } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

/**
 * What a traversed page CARRIED of the sequence of the noyau (noyau.md §9.1,
 * issue #152; maquette `SessionParcours`), drawn inside its card.
 *
 * - On a page of CHOICE: the two cards IN THE ORDER SHOWN, each with its noyau
 *   - name and colour, for the operator - what it was (the principal, the
 *   second, the retained or the opposite), and the text as served; the card
 *   taken; the version of the texts played.
 * - On the page of the FIRST choice, since #196 (analyste.md §8.5, planche
 *   `ParcoursEtats`): the pill « éliminé » on the card of a noyau the levels
 *   eliminated, and, when the second is one, the line « sans effet : le second est
 *   éliminé » that `noyauGapLineOf` writes - the card taken is recorded and changes
 *   nothing, the principal is retained.
 * - On the page of the FIRST choice, since issue #169 (§3.8, N74; planche
 *   `EcartSessionParcours`): the threshold frozen at the deal on the line of
 *   the block (« seuil 5 points », « sans seuil » for a first choice dealt
 *   before it), the outlined pill « retenu » on the card of the retained noyau
 *   - taken or not -, and under « Carte prise » the line of the gap, its pill
 *   in one of three states, in the words `noyauGapLineOf` writes for the
 *   markdown export too.
 * - On a page of PORTRAIT: the portrait served and the name `@noyau` rendered.
 *
 * The person saw none of the names, triplets or colours drawn here (N19, N28):
 * only the texts. This is the operator's screen.
 *
 * The texts are the rich text of the frozen version, painted by `renderRichText`
 * of `shared` - the one renderer of the Texte block, which escapes everything it
 * emits (`reader/blocks/text-block.tsx` says why that makes the markup safe).
 */

export interface WalkNoyauSectionProps {
  readonly site: WalkNoyauSite;
  readonly noyau: SessionNoyauReading;
}

export function WalkNoyauSection({ site, noyau }: WalkNoyauSectionProps) {
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.walkNoyauPage}
      data-mode={site.mode}
      className="flex flex-col gap-2.5"
    >
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-caption text-gray-600">
        <span>
          Bloc <b className="font-semibold text-gray-900">Noyau</b> · mode{' '}
          <b className="font-semibold text-gray-900">{NOYAU_MODE_LABEL[site.mode]}</b>
        </span>
        {site.mode === 'first-choice' && site.choice !== null ? (
          <>
            <span aria-hidden="true">·</span>
            <span
              data-testid={SESSIONS_TEST_IDS.walkNoyauThreshold}
              data-max-gap={site.choice.maxGap ?? undefined}
            >
              {site.choice.maxGap === null ? (
                <b className="font-semibold text-gray-900">sans seuil</b>
              ) : (
                <>
                  seuil{' '}
                  <b className="font-semibold text-gray-900">{noyauPoints(site.choice.maxGap)}</b>
                </>
              )}
            </span>
          </>
        ) : null}
        {noyau.texts === null ? null : (
          <>
            <span aria-hidden="true">·</span>
            <b className="font-semibold text-gray-900">
              Les textes des noyaux
              {noyau.texts.versionNumber === null ? '' : ` v${noyau.texts.versionNumber}`}
            </b>
            {noyau.texts.pin === null ? null : (
              <StatusBadge tone={noyau.texts.pin === 'latest' ? 'info' : 'neutral'}>
                {noyau.texts.pin === 'latest' ? 'latest' : `v${noyau.texts.pin}`}
              </StatusBadge>
            )}
          </>
        )}
      </p>

      {site.mode === 'portrait' ? (
        <PortraitServed noyau={noyau} />
      ) : site.choice === null ? (
        <p className="text-caption text-gray-600">
          Aucune carte n’a été distribuée sur cette page.
        </p>
      ) : (
        <ChoiceServed choice={site.choice} noyau={noyau} />
      )}
    </div>
  );
}

/**
 * The pill of the line of the gap, square with a radius of 4 px (planche
 * `EcartSessionParcours`): green when the card decided, grey when it changed
 * nothing, outlined in grey for a first choice shown before the threshold.
 */
const GAP_BADGE_CLASS: Readonly<Record<NoyauGapLineState, string>> = {
  decides: 'bg-vert-tint px-2 py-px text-vert-deep',
  'no-effect': 'bg-gray-100 px-2 py-px text-gray-700',
  // The second is eliminated (analyste.md §8.5, #196): « sans effet » too, in the same grey.
  'second-eliminated': 'bg-gray-100 px-2 py-px text-gray-700',
  'before-threshold': 'border border-gray-400 bg-white px-[7px] text-gray-700',
};

/** The line under « Carte prise » of a first choice (§9.1, N74), or nothing before a card is taken. */
function GapLine({
  choice,
  noyau,
}: {
  choice: NonNullable<WalkNoyauSite['choice']>;
  noyau: SessionNoyauReading;
}) {
  const line = noyauGapLineOf(noyau, choice);
  if (line === null) return null;
  const words: ReactNode[] = line.parts.map((part, index) =>
    part.strong ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
      <b key={index} className="font-semibold text-gray-900">
        {part.text}
      </b>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
      <Fragment key={index}>{part.text}</Fragment>
    ),
  );
  return (
    <p
      data-testid={SESSIONS_TEST_IDS.walkNoyauGap}
      data-state={line.state}
      className="flex items-baseline gap-2 text-caption text-gray-700"
    >
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-sm font-semibold text-micro leading-4',
          GAP_BADGE_CLASS[line.state],
        )}
      >
        {line.badge}
      </span>
      <span>{words}</span>
    </p>
  );
}

/** One noyau as the operator reads it: its colour, its name, its triplet. */
function NoyauName({ code, className }: { code: NoyauCode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span
        aria-hidden="true"
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: noyauOf(code).color }}
      />
      <span className="font-semibold text-gray-900">{noyauLabel(code)}</span>
    </span>
  );
}

/** A text of the frozen version, painted like a Texte block - or the sentence that says it is gone. */
function ServedText({ text }: { text: RichText | null }) {
  if (text === null) {
    return (
      <p className="text-gray-600 text-sm">
        Texte illisible : la version figée des textes ne se relit plus.
      </p>
    );
  }
  return (
    <div
      className="text-gray-800 text-sm leading-6 [&_li]:ml-5 [&_li]:list-disc [&_p+p]:mt-2 [&_ul]:my-1.5"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: escaped by construction - see the head.
      dangerouslySetInnerHTML={{ __html: renderRichText(text) }}
    />
  );
}

function ChoiceServed({
  choice,
  noyau,
}: {
  choice: NonNullable<WalkNoyauSite['choice']>;
  noyau: SessionNoyauReading;
}) {
  const retained = noyau.retained;
  const taken = takenCard(choice);
  // The noyaux the levels eliminated (analyste.md §6.4, #196): a card that stands for
  // one says so, on the first choice, where « sans effet : le second est éliminé »
  // needs a card to point at. None for a determination of before #196.
  const eliminated = eliminatedNoyaux(noyau.determination?.analysis ?? null);
  const [left, right] = choice.cards;
  const order = (lead: string) => (
    <>
      {lead} : 1. <b className="font-semibold text-gray-900">{noyauLabel(left.noyau)}</b> · 2.{' '}
      <b className="font-semibold text-gray-900">{noyauLabel(right.noyau)}</b>
    </>
  );

  return (
    <>
      <p className="text-caption text-gray-600">
        {choice.mode === 'second-choice' && retained !== null ? (
          <>
            Retenu <b className="font-semibold text-gray-900">{noyauLabel(retained)}</b> contre
            opposé{' '}
            <b className="font-semibold text-gray-900">{noyauLabel(oppositeNoyau(retained))}</b> ·{' '}
            {order('ordre montré')}
          </>
        ) : (
          order('Ordre montré')
        )}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {choice.cards.map((card, index) => {
          const isTaken = card.cardId === choice.takenCardId;
          return (
            <div
              key={card.cardId}
              data-testid={SESSIONS_TEST_IDS.walkNoyauCard}
              data-noyau={card.noyau}
              data-role={card.role ?? undefined}
              data-taken={isTaken ? 'true' : undefined}
              data-position={index + 1}
              className={cn(
                'flex flex-col gap-2 rounded-md bg-white p-3.5',
                isTaken ? 'border-2 border-vert' : 'border border-gray-200',
              )}
            >
              <div className="flex flex-wrap items-center gap-2 text-caption">
                <NoyauName code={card.noyau} />
                {card.role === null ? null : (
                  <StatusBadge
                    tone={
                      card.role === 'principal' || card.role === 'retained' ? 'accent' : 'neutral'
                    }
                  >
                    {CARD_ROLE_LABEL[card.role]}
                  </StatusBadge>
                )}
                {choice.mode === 'first-choice' && eliminated.includes(card.noyau) ? (
                  <StatusBadge
                    tone="neutral"
                    data-testid={SESSIONS_TEST_IDS.walkNoyauCardEliminated}
                  >
                    éliminé
                  </StatusBadge>
                ) : null}
                {/* The retained noyau at the first choice (§3.8, N74), taken or not:
                    outlined, so it is never read as the filled pill of a role. */}
                {choice.mode === 'first-choice' && card.noyau === retained ? (
                  <span
                    data-testid={SESSIONS_TEST_IDS.walkNoyauRetained}
                    className="inline-flex items-center whitespace-nowrap rounded-full border border-bleu-nuit bg-white px-[9px] py-px font-semibold text-bleu-nuit text-xs"
                  >
                    retenu
                  </span>
                ) : null}
                {isTaken ? (
                  <span className="ml-auto inline-flex items-center gap-1 rounded-pill bg-vert-tint px-2 py-0.5 font-semibold text-micro text-vert-deep">
                    <Check aria-hidden="true" className="size-3" strokeWidth={3} />
                    carte prise
                  </span>
                ) : null}
              </div>
              <ServedText text={card.text} />
            </div>
          );
        })}
      </div>

      <p className="text-caption text-gray-600">
        {taken === null ? (
          'Aucune carte confirmée : la session n’a pas quitté cette page par « Choisir ce texte ».'
        ) : (
          <>
            Carte prise : <b className="font-semibold text-gray-900">{noyauLabel(taken.noyau)}</b>
            {taken.role === null ? '' : `, ${CARD_ROLE_ARTICLE[taken.role]}`}.
          </>
        )}
      </p>
      <GapLine choice={choice} noyau={noyau} />
    </>
  );
}

function PortraitServed({ noyau }: { noyau: SessionNoyauReading }) {
  const retained = noyau.retained;
  if (retained === null) {
    return (
      <p className="text-caption text-gray-600">
        Aucun noyau retenu : le portrait n’avait rien à montrer.
      </p>
    );
  }
  return (
    <>
      <p className="text-caption text-gray-600">
        <code className="rounded-sm bg-violet-tint px-1 font-mono text-violet-deep">@noyau</code>{' '}
        rendu : <b className="font-semibold text-gray-900">{noyauOf(retained).name}</b>
      </p>
      <div
        data-testid={SESSIONS_TEST_IDS.walkNoyauPortrait}
        data-noyau={retained}
        className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3.5"
      >
        <p className="flex flex-wrap items-center gap-1.5 text-caption">
          <NoyauName code={retained} />
          <span className="text-gray-600">· portrait servi</span>
        </p>
        <ServedText text={noyau.portrait} />
      </div>
    </>
  );
}
